import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Box, CircularProgress, TextField, Typography } from "@mui/material";
import { searchReportReps } from "../../../../services/reports";

const normalizeRole = (role = "") => String(role || "").replace(/_/g, "-").trim().toLowerCase();
const roleAllowed = (role = "", allowedRoles = []) => {
  const normalizedRole = normalizeRole(role);
  const normalizedAllowed = allowedRoles.map((item) => normalizeRole(item));
  return normalizedAllowed.includes(normalizedRole);
};
const repOptionLabel = (option) => option?.label || [option?.first_name, option?.last_name].filter(Boolean).join(" ").trim() || option?.email || "";

export default function ReportRepAsyncSelect({
  instanceUniqueId,
  value,
  onChange,
  searchFn = searchReportReps,
  roles = ["client-rep", "order-rep", "administrator", "admin"],
  seedOptions = [],
  label = "Rep",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState(Array.isArray(seedOptions) ? seedOptions : []);
  const [loading, setLoading] = useState(false);
  const requestSeq = useRef(0);

  useEffect(() => {
    setInputValue(value ? repOptionLabel(value) : "");
  }, [value]);

  useEffect(() => {
    setOptions((previous) => {
      const map = new Map(previous.map((option) => [String(option?.id || ""), option]));
      (Array.isArray(seedOptions) ? seedOptions : []).forEach((option) => {
        if (!option?.id || !roleAllowed(option.role, roles)) return;
        map.set(String(option.id), option);
      });
      if (value?.id && roleAllowed(value.role, roles)) map.set(String(value.id), value);
      return Array.from(map.values()).filter((option) => option?.id && roleAllowed(option.role, roles));
    });
  }, [seedOptions, value, roles]);

  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(async () => {
      const requestId = ++requestSeq.current;
      setLoading(true);
      try {
        const res = await searchFn({
          instanceUniqueId,
          q: inputValue,
          page: 1,
          limit: 25,
          roles,
        });
        if (requestId !== requestSeq.current) return;
        const reps = Array.isArray(res?.reps) ? res.reps : [];
        setOptions((previous) => {
          const map = new Map();
          reps.forEach((rep) => {
            if (!rep?.id || !roleAllowed(rep.role, roles)) return;
            map.set(String(rep.id), rep);
          });
          (Array.isArray(seedOptions) ? seedOptions : []).forEach((option) => {
            if (!option?.id || !roleAllowed(option.role, roles) || map.has(String(option.id))) return;
            map.set(String(option.id), option);
          });
          if (value?.id && !map.has(String(value.id)) && roleAllowed(value.role, roles)) map.set(String(value.id), value);
          return Array.from(map.values());
        });
      } catch (error) {
        if (requestId !== requestSeq.current) return;
        console.error("Failed to search report reps", error);
      } finally {
        if (requestId === requestSeq.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [open, inputValue, instanceUniqueId, roles, searchFn, seedOptions, value]);

  return (
    <Autocomplete
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={value}
      disabled={disabled}
      loading={loading}
      filterOptions={(x) => x}
      inputValue={inputValue}
      onInputChange={(_, nextValue, reason) => {
        if (reason === "reset") {
          setInputValue(value ? repOptionLabel(value) : "");
          return;
        }
        if (reason === "clear") {
          setInputValue("");
          return;
        }
        setInputValue(nextValue);
      }}
      onChange={(_, option) => {
        setInputValue(option ? repOptionLabel(option) : "");
        onChange(option || null);
      }}
      isOptionEqualToValue={(option, currentValue) => String(option?.id || "") === String(currentValue?.id || "")}
      getOptionLabel={repOptionLabel}
      renderOption={(props, option) => (
        <li {...props} key={option?.id || option?.label}>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Typography variant="body2" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {repOptionLabel(option)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {[option?.roleLabel, option?.email].filter(Boolean).join(" - ")}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search reps"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
